import {TreeNode} from '@hconnect/common/types'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {IconButton, Stack, TextField, Typography} from '@mui/material'
import {useState} from 'react'

import {useDeleteSignal} from '../../hooks/useDeleteSignal'
import {SignalListData} from '../../types/signal'

import {useGetSignals} from './api/useGetSignals'

import {ListData, StateList} from '@settings/modules/common/components'

const getSignalPath = (signal: SignalListData): string => {
  const {area, productionLine, department, equipment} = signal
  return `${area.name} > ${productionLine.name} > ${department.name} > ${equipment.name}`
}

interface SignalListProps {
  selectedItem: TreeNode
  onDetailClick: (signalId: string) => void
  onAddClick: () => void
}

export const SignalList: React.FC<SignalListProps> = ({
  selectedItem,
  onDetailClick,
  onAddClick
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('')

  const {mutate: deleteSignal} = useDeleteSignal()
  const {
    data: signals,
    isLoading,
    isFetching
  } = useGetSignals<ListData<string>>(
    {
      entityType: selectedItem.itemType,
      entityId: selectedItem.upmId,
      searchTerm
    },
    {
      select: (data): ListData<string>[] =>
        data.map(
          (signal): ListData<string> => ({
            id: signal.id,
            title: signal.localName,
            label: getSignalPath(signal)
          })
        )
    }
  )

  return (
    <Stack spacing={1} pb={0} px={0}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" pl={3} pb={1}>
        <Stack direction="row" alignItems="center">
          <Typography variant="h2">
            {`${searchTerm ? 'Filtered' : 'All'} (${signals?.length ?? 0})`}
          </Typography>
          <IconButton data-test-id={'upm-add-sensor-button'} color="primary" onClick={onAddClick}>
            <AddIcon />
          </IconButton>
        </Stack>
        <TextField
          label="Filter"
          variant="filled"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Stack>

      <StateList
        data={signals}
        isLoading={isLoading}
        isFetching={isFetching}
        secondaryAction={({id, title}) => (
          <IconButton
            edge="end"
            aria-label="delete"
            color="error"
            // delete is disabled for now, no clear business requirements
            disabled={!title?.toUpperCase()?.includes('TEST')}
            onClick={() => deleteSignal(id)}
          >
            <DeleteOutlineIcon />
          </IconButton>
        )}
        onClick={(id) => onDetailClick(id)}
        data-test-id="signal-list-item"
      />
    </Stack>
  )
}
